import { useEffect, useRef, useState } from 'react'
import { useMainButton } from '../components/MainButton'
import { adsAvailable, showRewardAd } from '../lib/ads'
import { RewardModal } from '../components/RewardModal'
import { api } from '../lib/api'
import { haptic, tg } from '../lib/telegram'
import { authMode } from '../lib/auth'
import { shareInvite } from '../lib/referral'
import { FREE_LIMIT_PER_DAY, useSirr } from '../store'
import { SPREADS, spreadDesc, spreadName } from '../data/spreads'
import { FORMATS, formatDesc, formatName } from '../data/formats'
import { PACKAGES, PWA_PACKAGES, packageName, type PwaPackage, type StarPackage } from '../../../shared/packages.ts'
import { track } from '../lib/analytics'
import { formatDate } from '../i18n'

export function Stars({ onLogin }: { onLogin: () => void }) {
  // Сколько людей вообще доходит до экрана покупки — половина воронки.
  useEffect(() => {
    track('stars_open')
  }, [])

  const { profile, lang, t, update, setRemaining, anon } = useSirr()

  // Реферальный код для блока «пригласи вместо оплаты».
  /**
   * Условия подписки приходят с сервера: цена и числа настраиваются
   * окружением, и зашитые здесь обещали бы не то.
   */
  const [sub, setSub] = useState<{
    stars: number
    perDay: number
    dailyStars: number
    includedPerDay: number
    includedLeft: number
    plans: { id: string; days: number; price: number; full: number; best: boolean; available: boolean }[]
    introUntil: number
    subscribed: boolean
    subUntil: number
    available: boolean
  } | null>(null)
  const [subbing, setSubbing] = useState(false)
  useEffect(() => {
    api.subscription().then(setSub).catch(() => {})
  }, [])

  /**
   * Оформление подписки.
   *
   * Что произойдёт, решает сервер: пробный период он выдаёт сразу (`granted`),
   * платный план отдаёт счётом. Цену клиент не считает и не присылает — иначе
   * достаточно было бы сказать «у меня скидка».
   *
   * После оплаты состояние перечитываем, а не выводим сами: срок ставит вебхук,
   * и своей арифметике тут верить нельзя.
   */
  const subscribe = async (plan: string) => {
    if (subbing) return
    setSubbing(true)
    try {
      const res = await api.subscribe(plan)
      if (res.kind === 'granted') {
        haptic.notify('success')
        update({ subscribed: true })
        void api.subscription().then(setSub).catch(() => {})
        return
      }
      tg?.openInvoice(res.value, (status) => {
        if (status === 'paid') {
          void api.subscription().then(setSub).catch(() => {})
          haptic.notify('success')
        } else if (status === 'failed') {
          haptic.notify('error')
        }
      })
    } catch {
      haptic.notify('error')
    } finally {
      setSubbing(false)
    }
  }

  /**
   * Есть ли что показать тому, кто оформить платное не может. Пробный период
   * платежом не является, поэтому доступен и в браузере — а без этой проверки
   * блок подписки исчезал в PWA целиком, вместе с бесплатными тремя днями.
   */
  const trialOffered = Boolean(sub?.plans.some((p) => p.available && p.price === 0))

  /** Название плана — из общих строк, а не из ответа сервера: он их не переводит. */
  const planLabel = (id: string) =>
    id === 'trial' ? t('planTrial')
    : id === 'week' ? t('planWeek')
    : id === 'year' ? t('planYear')
    : t('planMonth')


  /**
   * Обратный отсчёт окна новичка. Тикаем раз в секунду, пока оно открыто;
   * когда истечёт — блок пропадает сам, и перерисовывать больше нечего.
   */
  const [now, setNow] = useState(() => Date.now())
  const bonusUntil = profile.bonusUntil ?? 0
  const ticking = Math.max(bonusUntil, profile.introUntil ?? 0)
  useEffect(() => {
    if (ticking <= Date.now()) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])

  const bonusLeft = (() => {
    const ms = bonusUntil - now
    if (ms <= 0) return null
    const total = Math.ceil(ms / 1000)
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
  })()
  /**
   * Обратный отсчёт льготной цены. Тикает тот же `now`, что и у окна новичка:
   * второй интервал ради того же самого — лишняя перерисовка раз в секунду.
   */
  const introUntil = profile.introUntil ?? 0
  const introLeft = (() => {
    const ms = introUntil - now
    if (ms <= 0) return null
    const total = Math.ceil(ms / 1000)
    const h = Math.floor(total / 3600)
    return `${h}:${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  })()

  /**
   * Звёзды за рекламу.
   *
   * Числа держит сервер (`config.ads`), клиент их только показывает: они
   * настраиваются окружением, и зашитое здесь обещало бы не то, что начислится.
   * Начисляет тоже сервер — по обратному вызову AdsGram, а не по слову клиента.
   */
  const [ads, setAds] = useState<{ stars: number; left: number } | null>(null)
  const [watching, setWatching] = useState(false)
  const [adNote, setAdNote] = useState<string | null>(null)
  /** Сколько звёзд принёс последний ролик; 0 — окна нет. */
  const [won, setWon] = useState(0)
  useEffect(() => {
    if (!adsAvailable()) return
    api.me().then((me) => setAds(me.ads ?? null)).catch(() => {})
  }, [])

  /**
   * Досмотренный ролик подтверждает сервер AdsGram обратным вызовом, а не этот
   * код. Постбэк идёт server-to-server и отстаёт на секунду-другую, поэтому
   * после ролика мы не начисляем, а переспрашиваем сервер, пока баланс не
   * вырастет, — тем же способом, что и после оплаты.
   */
  const watchAd = async () => {
    if (watching || !ads || ads.left <= 0) return
    setWatching(true)
    setAdNote(null)
    balanceBeforePay.current = profile.stars
    try {
      if (!(await showRewardAd())) {
        // Ролика не оказалось, блокировщик, закрыл на середине — всё это не
        // поломка, и разбирать причину человеку незачем.
        setAdNote(t('earnNone'))
        return
      }
      await refreshBalance(20_000)
      const me = await api.me()
      setAds(me.ads ?? null)
      update({ stars: me.profile.stars ?? profile.stars })
      // Сколько именно начислено — считаем по разнице, а не по обещанному
      // числу: у потолка последний ролик может не дать ничего.
      const got = (me.profile.stars ?? 0) - balanceBeforePay.current
      // Начислили — показываем окно; не начислили (упёрлись в суточный
      // потолок) — спокойную строку. Поздравлять там не с чем.
      if (got > 0) setWon(got)
      else setAdNote(t('earnOut'))
    } catch {
      setAdNote(t('earnNone'))
    } finally {
      setWatching(false)
    }
  }

  const [refCode, setRefCode] = useState<string | null>(null)
  // Сколько дают за друга — число с сервера, а не зашитое здесь: оно
  // настраивается переменной окружения, и разъехавшийся текст обещал бы не то.
  const [refStars, setRefStars] = useState(0)
  const [invCopied, setInvCopied] = useState(false)
  useEffect(() => {
    if (anon) return // анониму код не выдаётся: приглашать он не может
    api.referral().then((r) => {
      setRefCode(r.code)
      setRefStars(r.perFriend)
    }).catch(() => {})
  }, [anon])
  const shareRef = async () => {
    if (!refCode) return
    haptic.select()
    if ((await shareInvite(refCode, t('inviteMsg'))) === 'copied') {
      setInvCopied(true)
      setTimeout(() => setInvCopied(false), 2000)
    }
  }
  // Список платных раскладов берём из тех же данных, что и остальное
  // приложение: обещание на экране оплаты не может разойтись с тем, что
  // человек реально получит.
  const paidSpreads = SPREADS.filter((s) => s.available && s.cost > 0)
  const paidFormats = FORMATS.filter((f) => f.available && f.cost > 0)
  // Самый дорогой расклад — якорь для сравнения с ценой подписки. Считаем по
  // данным, а не пишем числом: иначе разойдётся с прайсом при первой правке.
  const dearestSpread = Math.max(...paidSpreads.map((s) => s.cost))
  const isPwa = authMode === 'pwa'
  // Наборы пакетов у платформ разные: у lava минимум $5, поэтому в PWA свои
  // пакеты (от 500 ✦), в Telegram — прежние (от 100 ✦).
  const pkgs: (StarPackage | PwaPackage)[] = isPwa ? PWA_PACKAGES : PACKAGES
  const [selected, setSelected] = useState(isPwa ? 1500 : 500)
  const [paying, setPaying] = useState(false)
  const selectedPkg = pkgs.find((p) => p.stars === selected) ?? pkgs[0]

  /**
   * После оплаты звёзды начисляет вебхук, а не клиент. Он может прийти чуть
   * позже, чем человек вернулся в приложение, поэтому опрашиваем баланс, пока
   * он не вырастет или не выйдет время. В PWA оплата идёт на отдельной странице
   * и занимает дольше, поэтому окно опроса шире.
   */
  const refreshBalance = async (maxMs: number) => {
    const until = Date.now() + maxMs
    let step = 1000
    while (Date.now() < until) {
      try {
        const me = await api.me()
        if (typeof me.profile.stars === 'number') {
          update({ stars: me.profile.stars })
          setRemaining(me.remaining)
          if (me.profile.stars > balanceBeforePay.current) {
            haptic.notify('success')
            return
          }
        }
      } catch {
        /* сеть подождёт до следующей попытки */
      }
      await new Promise((r) => setTimeout(r, step))
      step = Math.min(step + 500, 4000)
    }
  }

  const balanceBeforePay = useRef(profile.stars)

  const pay = async () => {
    balanceBeforePay.current = profile.stars
    setPaying(true)
    try {
      track('invoice_open', { pkg: selected })
      const { kind, value } = await api.invoice(selected, lang)

      if (kind === 'invoice' && tg) {
        // Telegram Stars: нативное окно оплаты.
        tg.openInvoice(value, (status) => {
          if (status === 'failed') {
            haptic.notify('error')
            return
          }
          if (status !== 'paid') return
          // Баланс спрашиваем у сервера, а не прибавляем сами: звёзды начисляет
          // вебхук, и только он знает про бонус пакета.
          void refreshBalance(20_000)
        })
      } else {
        // PWA (lava): оплата на отдельной странице. Открываем её, а вернувшись,
        // человек увидит обновлённый баланс — опрашиваем сервер, пока вебхук
        // не начислит.
        const win = window.open(value, '_blank')
        if (!win) window.location.href = value // всплывашка заблокирована — ведём в этой же вкладке
        void refreshBalance(180_000)
      }
    } catch {
      haptic.notify('error')
    } finally {
      setPaying(false)
    }
  }

  const payLabel = isPwa ? `${t('pay')} $${(selectedPkg as PwaPackage).usd}` : `${t('pay')} ${selected} ✦`
  useMainButton({ label: payLabel, loading: paying, onClick: () => void pay() })

  return (
    <div className="screen">
      {/* Окно поверх экрана, а не строка сбоку: человек возвращается из
          полноэкранного ролика, и мелкую надпись на прежнем месте не ищет. */}
      <RewardModal
        stars={won}
        title={t('rewardTitle')}
        body={t('rewardBody', { n: won })}
        ok={t('rewardOk')}
        onClose={() => setWon(0)}
      />
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div
          style={{
            width: 70,
            height: 70,
            margin: '0 auto 12px',
            borderRadius: '50%',
            border: '1px solid rgba(200,162,78,.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 32,
            color: 'var(--gold)',
            boxShadow: 'var(--gold-glow)',
            background: 'radial-gradient(circle, rgba(74,44,20,.5), rgba(18,10,31,.8))',
          }}
        >
          ♛
        </div>
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 26 }}>
          {t('premiumTitle')}
        </div>
        <p className="subtitle" style={{ margin: '4px 0 0' }}>
          {t('premiumIntro')}
        </p>
      </div>

      {/* Окно повышенного начисления. Обратный отсчёт рисуем от срока, который
          прислал сервер: своя арифметика дала бы на двух устройствах два
          разных «осталось», а закрывает окно всё равно он. */}
      {bonusLeft && (
        <div
          style={{
            border: '1px solid var(--line-strong)',
            borderRadius: 16,
            padding: '12px 14px',
            marginBottom: 18,
            textAlign: 'center',
            background: 'linear-gradient(120deg, rgba(74,44,20,.55), rgba(18,10,31,.35))',
          }}
        >
          <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 18, color: 'var(--gold-bright)' }}>
            {t('bonusWindowTitle')}
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 20, color: 'var(--gold)', marginTop: 4 }}>
            {t('bonusWindowLeft', { t: bonusLeft })}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 6, lineHeight: 1.4 }}>
            {t('bonusWindowNote')}
          </div>
        </div>
      )}

      {/* Подписка — главное предложение экрана, и потому она стоит ПЕРВОЙ и
          выглядит крупнее пакетов. Разовые звёзды остались ниже как запасной
          путь: продаём мы подписчика, а не покупателя пакета.

          Показываем только там, где её можно оформить: платёж рекуррентный, и
          делает его Telegram Stars. В браузере оформить нечем, и предлагать то,
          чего нет, хуже, чем не предлагать. */}
      {/* Анониму предложение подписки не показываем: оформить он её не сможет
          (`/api/subscribe` закрыт `requireAccount`), а «три дня бесплатно»,
          которые молча не срабатывают, — обман. Ему показано предложение войти. */}
      {sub && !anon && (sub.available || sub.subscribed || trialOffered) && (
        <div
          style={{
            border: `1px solid ${sub.subscribed ? 'var(--line-strong)' : 'rgba(200,162,78,.55)'}`,
            borderRadius: 18,
            padding: '16px 16px 14px',
            marginBottom: 20,
            background: 'linear-gradient(140deg, rgba(96,58,24,.55), rgba(18,10,31,.4))',
            boxShadow: sub.subscribed ? 'none' : 'var(--gold-glow)',
          }}
        >
          <div className="eyebrow" style={{ marginBottom: 8, color: 'var(--gold)' }}>
            {t('subTitle')}
          </div>

          {sub.subscribed ? (
            <>
              <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 19 }}>
                {t('subDesc', { n: sub.includedPerDay })}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 6, lineHeight: 1.45 }}>
                {t('subActive', { d: formatDate(sub.subUntil, lang) })}
              </div>
              {/* Остаток на сегодня: подписчик должен видеть, что у него есть,
                  а не выяснять это, упёршись в цену. */}
              <div style={{ fontSize: 12, color: 'var(--gold)', marginTop: 4 }}>
                {t('subLeft', { n: sub.includedLeft })}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 21, lineHeight: 1.25 }}>
                {t('subDesc', { n: sub.includedPerDay })}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-soft, #c9bcd8)', marginTop: 6 }}>
                {t('subPerks')}
              </div>
              {/* Якорь цены: одно «Колесо года» стоит почти как месяц подписки.
                  Число берём из самих данных — в строке его писать нельзя, оно
                  разойдётся с прайсом при первой правке. */}
              <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 8, lineHeight: 1.45 }}>
                {t('subCompare', { c: dearestSpread })}
              </div>
              {/* Обратный отсчёт льготной цены прямо над планами: он и есть
                  причина решать сейчас, а не «когда-нибудь». */}
              {introLeft && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '7px 10px',
                    borderRadius: 10,
                    textAlign: 'center',
                    border: '1px solid rgba(200,162,78,.4)',
                    background: 'rgba(120,74,28,.35)',
                  }}
                >
                  <span style={{ fontSize: 11.5, color: 'var(--ink-soft, #c9bcd8)' }}>
                    {t('introStrip')}{' '}
                  </span>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--gold-bright, #e8c877)' }}>
                    {introLeft}
                  </span>
                </div>
              )}

              {/* Планы. Пробный период стоит первым: он ничего не стоит, и
                  начинать разговор о деньгах с бесплатного честнее. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 12 }}>
                {sub.plans.filter((p) => p.available && (sub.available || p.price === 0)).map((p) => {
                  const free = p.price === 0
                  const cut = !free && p.price < p.full
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => void subscribe(p.id)}
                      disabled={subbing}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        width: '100%',
                        textAlign: 'left',
                        border: `1px solid ${p.best ? 'rgba(200,162,78,.55)' : 'var(--line)'}`,
                        borderRadius: 13,
                        padding: '12px 14px',
                        background: p.best ? 'rgba(120,74,28,.3)' : 'rgba(30,17,52,.5)',
                        color: 'var(--ink)',
                        fontFamily: 'var(--sans)',
                        opacity: subbing ? 0.6 : 1,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14.5, fontWeight: 600 }}>{planLabel(p.id)}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 2 }}>
                          {free ? t('planTrialNote') : p.id === 'month' && !cut ? t('planRenews') : t('planOnce')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 15, color: 'var(--gold)', whiteSpace: 'nowrap' }}>
                          {free ? t('planFree') : `✦ ${p.price}`}
                        </div>
                        {/* Полная цена зачёркнутой — иначе «149» не читается
                            как скидка и сравнивать не с чем. */}
                        {cut && (
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--ink-faint)',
                              textDecoration: 'line-through',
                            }}
                          >
                            ✦ {p.full}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Платные планы оформляются только в Telegram: платёж идёт
                  звёздами. Вне Telegram остаётся пробный период — он не
                  платёж вовсе, — и об остальном честнее сказать прямо, чем
                  показать кнопку, которая ничего не сделает. */}
              {!sub.available && (
                <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 10, lineHeight: 1.45 }}>
                  {t('subTelegramOnly')}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Звёзды за рекламу — бесплатный путь для тех, кто платить не станет.
          Стоит рядом с приглашениями и ДО пакетов, но отдельной строкой: это
          не скидка на покупку, а другой способ добыть ту же валюту. Подписчику
          не показывается вовсе — сервер присылает ему нулевой остаток. */}
      {ads && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            border: '1px solid var(--line-strong)',
            borderRadius: 14,
            padding: '12px 14px',
            marginBottom: 18,
            background: 'linear-gradient(120deg, rgba(74,44,20,.4), rgba(18,10,31,.35))',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{t('earnTitle')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 2 }}>
              {adNote ?? (ads.left > 0 ? t('earnDesc', { n: ads.stars }) : t('earnOut'))}
            </div>
            {ads.left > 0 && !adNote && (
              <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4 }}>
                {t('earnLeft', { n: ads.left })}
              </div>
            )}
          </div>
          {ads.left > 0 && (
            <button
              onClick={() => void watchAd()}
              disabled={watching}
              style={{
                flexShrink: 0,
                background: 'var(--gold-gradient)',
                color: '#1a1020',
                border: 'none',
                borderRadius: 10,
                padding: '9px 14px',
                fontFamily: 'var(--sans)',
                fontSize: 13,
                fontWeight: 700,
                opacity: watching ? 0.6 : 1,
                cursor: 'pointer',
              }}
            >
              {watching ? '…' : t('earnGo')}
            </button>
          )}
        </div>
      )}

      {/* Пригласить вместо оплаты — прямо на пейволле. */}
      {refCode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            border: '1px solid var(--line-strong)',
            borderRadius: 14,
            padding: '12px 14px',
            marginBottom: 18,
            background: 'linear-gradient(120deg, rgba(74,44,20,.4), rgba(18,10,31,.35))',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 600 }}>{t('paywallInviteTitle')}</div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-muted)', marginTop: 2 }}>
              {t('paywallInviteDesc', { bonus: refStars })}
            </div>
          </div>
          <button
            onClick={() => void shareRef()}
            style={{
              flexShrink: 0,
              background: 'var(--gold-gradient)',
              color: '#1a1020',
              borderRadius: 10,
              padding: '9px 14px',
              fontFamily: 'var(--sans)',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {invCopied ? t('inviteCopied') : t('inviteShare')}
          </button>
        </div>
      )}

      <div className="eyebrow" style={{ marginBottom: 12 }}>
        {t('whatStarsBuy')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 11, marginBottom: 16 }}>
        {/* Гадания без карт стоят дороже раскладов — им место в начале списка,
            а не хвостом после двенадцати позиций Таро. */}
        {paidFormats.map((f) => (
          <div
            key={f.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px solid rgba(200,162,78,.18)',
              borderRadius: 13,
              padding: '13px 14px',
              background: 'rgba(30,17,52,.45)',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 9,
                border: '1px solid rgba(200,162,78,.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                color: 'var(--gold)',
              }}
            >
              {f.sym}
            </div>
            <div style={{ flex: 1, lineHeight: 1.3 }}>
              <div style={{ fontSize: 14, color: '#e2d6ef' }}>{formatName(f, lang)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 2 }}>
                {formatDesc(f, lang)}
              </div>
            </div>
            <div style={{ fontSize: 14, color: 'var(--gold)', whiteSpace: 'nowrap' }}>✦ {f.cost}</div>
          </div>
        ))}

        {paidSpreads.map((sp) => (
          <div
            key={sp.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              border: '1px solid rgba(200,162,78,.18)',
              borderRadius: 13,
              padding: '13px 14px',
              background: 'rgba(30,17,52,.45)',
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 9,
                border: '1px solid rgba(200,162,78,.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                color: 'var(--gold)',
              }}
            >
              {sp.sym}
            </div>
            <div style={{ flex: 1, lineHeight: 1.3 }}>
              <div style={{ fontSize: 14, color: '#e2d6ef' }}>{spreadName(sp, lang)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginTop: 2 }}>
                {t('cardsCount', { n: sp.n })} · {spreadDesc(sp, lang)}
              </div>
            </div>
            <div style={{ fontSize: 14, color: 'var(--gold)', whiteSpace: 'nowrap' }}>✦ {sp.cost}</div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 11.5, color: 'var(--ink-faint)', lineHeight: 1.45, margin: '0 0 22px' }}>
        {t('freeTierNote', { n: FREE_LIMIT_PER_DAY })}
      </p>

      <div className="eyebrow" style={{ marginBottom: 12 }}>
        {t('buyStars')}
      </div>

      {/*
        Тому, кто ещё не назвался, покупку не показываем вовсе — вместо неё
        объяснение и вход.

        Сервер такую покупку и так не пропустит (`requireAccount` отвечает 403
        `login_required`): на анонимном аккаунте денег не бывает. Но упереться в
        отказ ПОСЛЕ выбора пакета и нажатия «купить» — это узнать правило самым
        обидным способом. Причина названа честно: звёзды привязаны к аккаунту,
        без него они пропадут вместе с приложением.
      */}
      {anon && (
        <div
          style={{
            border: '1px solid var(--line)',
            borderRadius: 14,
            padding: '16px 18px',
            marginBottom: 22,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{t('firstFreeLogin')}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginTop: 6, lineHeight: 1.45 }}>
            {t('anonFreeWhy')}
          </div>
          <button
            type="button"
            onClick={onLogin}
            style={{
              marginTop: 14,
              width: '100%',
              background: 'transparent',
              color: 'var(--ink)',
              border: '1px solid var(--line-strong)',
              borderRadius: 12,
              padding: '11px 20px',
              fontFamily: 'var(--sans)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('enter')}
          </button>
        </div>
      )}

      <div style={{ display: anon ? 'none' : 'flex', gap: 10 }}>
        {pkgs.map((p) => (
          <button
            key={p.stars}
            type="button"
            className="pick"
            aria-pressed={selected === p.stars}
            style={{ flex: 1, textAlign: 'center', borderRadius: 15, padding: '16px 8px', position: 'relative' }}
            onClick={() => {
              haptic.select()
              setSelected(p.stars)
            }}
          >
            {p.best && (
              <div
                style={{
                  position: 'absolute',
                  top: -9,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  fontSize: 8,
                  letterSpacing: 0.8,
                  textTransform: 'uppercase',
                  background: 'var(--gold-gradient)',
                  color: '#160c28',
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('popular')}
              </div>
            )}
            <div style={{ fontSize: 22, color: 'var(--gold)', marginBottom: 5 }}>✦</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 22 }}>{p.stars}</div>
            <div style={{ fontSize: 11, color: '#c9bcd8', marginTop: 2 }}>{packageName(p, lang)}</div>
            {/* В Telegram выгода — «+бонус»; в PWA — цена в долларах (бонуса нет,
                выгода зашита в цену за звезду). */}
            {isPwa ? (
              <div style={{ fontSize: 15, color: 'var(--ink)', marginTop: 8, fontWeight: 700 }}>
                ${(p as PwaPackage).usd}
              </div>
            ) : (
              <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 5, minHeight: 13 }}>
                {(p as StarPackage).bonus > 0 ? `+${(p as StarPackage).bonus} ✦ ${t('bonus')}` : ''}
              </div>
            )}
          </button>
        ))}
      </div>

      <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-faint)', margin: '16px 0 0', lineHeight: 1.4 }}>
        {authMode === 'pwa' ? t('starsNotePwa') : t('starsNote')}
      </p>
    </div>
  )
}
